// TanStack Query hooks for every backend endpoint the UI consumes.
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import type { ClientRecord } from '../lib/api'
import { api } from './client'
import type {
  AliasGroup,
  ClearedList,
  CreditAtRisk,
  DifferencePageData,
  DifferenceSummary,
  MatchSuggestions,
  ReconciliationState,
  TimingData,
  DocumentInfo,
  FormatProfile,
  GridPage,
  InspectResult,
  PageOf,
  Party,
  PartyDetail,
  Period,
  PeriodSummary,
  ProfileFields,
  CompanyOffers,
  DiffCompany,
  RawCompaniesPage,
  RawRowsPage,
  RawSummaryEntry,
  ReportsSummary,
  Run,
  Upload,
} from './types'

const ACTIVE: ReadonlySet<string> = new Set(['queued', 'running'])

/** The taxpayer the workspace is open on. The predecessor's screens found the
 *  name by scanning `/entities`; this repo's noun is `client` (ADR-0002) and
 *  the single read beats a paginated list scanned for one row. Doubles as the
 *  existence probe: a cross-tenant clientId 404s here instead of rendering an
 *  empty-but-plausible workspace. */
export function useClient(clientId: string) {
  return useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api<ClientRecord>(`/clients/${clientId}`),
    enabled: Boolean(clientId),
    retry: false,
  })
}

export function usePeriods(clientId: string) {
  return useQuery({
    queryKey: ['periods', clientId],
    queryFn: () => api<Period[]>(`/clients/${clientId}/periods`),
    enabled: Boolean(clientId),
  })
}

export function usePeriodSummary(periodId: string) {
  return useQuery({
    queryKey: ['period-summary', periodId],
    queryFn: () => api<PeriodSummary>(`/periods/${periodId}/summary`),
    enabled: Boolean(periodId),
  })
}

export interface DifferenceParams {
  status?: string
  date_flag?: string
  party_flag?: string
  sec_flag?: string
  search?: string
  bucket?: string
  party?: string
  sort?: string
  dir?: string
  page: number
  size: number
}

export function useDifference(runId: string | null, params: DifferenceParams) {
  const qs = new URLSearchParams()
  for (const k of ['status', 'date_flag', 'party_flag', 'sec_flag', 'search', 'bucket', 'party', 'sort', 'dir'] as const) {
    const v = params[k]
    if (v) qs.set(k, v)
  }
  qs.set('page', String(params.page))
  qs.set('size', String(params.size))
  return useQuery({
    queryKey: ['difference', runId, qs.toString()],
    queryFn: () => api<DifferencePageData>(`/runs/${runId}/difference?${qs}`),
    enabled: runId != null,
    placeholderData: keepPreviousData,
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
  })
}

/** `bucket` omitted = every party in the run, which is what the All view of
 *  the merged surface needs. A filter that can only list the parties of one
 *  bucket, sitting above a grid showing both, is worse than no filter. */
/** The companies with work left, ranked by what is still apart. SQL
 *  aggregates: asking the worklist for the same thing runs the combination
 *  search for every party in the run — about a minute on a real engagement. */
export function useDiffCompanies(runId: string | null, search: string,
                                 sort: 'amount' | 'name' = 'amount') {
  const qs = new URLSearchParams({ size: '500', sort })
  if (search) qs.set('search', search)
  return useQuery({
    queryKey: ['diff-companies', runId, search, sort],
    queryFn: () =>
      api<{ total: number; items: DiffCompany[] }>(
        `/runs/${runId}/difference/companies?${qs}`),
    enabled: runId != null,
    placeholderData: keepPreviousData,
  })
}

/** What the engine can offer for ONE company. The search lives here because it
 *  is affordable one party at a time and not three hundred. */
export function useCompanyOffers(runId: string | null, party: string | null) {
  return useQuery({
    queryKey: ['company-offers', runId, party],
    queryFn: () =>
      api<CompanyOffers>(
        `/runs/${runId}/difference/companies/offers?party=${encodeURIComponent(party ?? '')}`),
    enabled: runId != null && !!party,
    staleTime: 30_000,
  })
}

export function useDifferenceParties(
  runId: string | null,
  bucket?: string,
  search?: string,
) {
  // the filter answers the same question as the search box beside it
  const qs = new URLSearchParams()
  if (bucket) qs.set('bucket', bucket)
  if (search) qs.set('search', search)
  return useQuery({
    queryKey: ['difference-parties', runId, bucket ?? 'all', search ?? ''],
    queryFn: () =>
      api<{ parties: { party: string; n: number; diff?: string }[] }>(
        `/runs/${runId}/difference/parties?${qs}`,
      ),
    enabled: runId != null,
    placeholderData: keepPreviousData,
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
  })
}

export function useDifferenceSummary(runId: string | null) {
  return useQuery({
    queryKey: ['difference-summary', runId],
    queryFn: () => api<DifferenceSummary>(`/runs/${runId}/difference/summary`),
    enabled: runId != null,
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
  })
}

export function useMatchSuggestions(runId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['match-suggestions', runId],
    queryFn: () =>
      api<MatchSuggestions>(`/runs/${runId}/difference/match-suggestions`),
    enabled: runId != null && enabled,
    // matching is expensive; keep the result cached so re-opening the tab
    // doesn't recompute — the user re-runs it explicitly via "Reconcile again"
    staleTime: Infinity,
  })
}

export function useCleared(runId: string | null, page = 1, size = 100) {
  return useQuery({
    queryKey: ['cleared', runId, page, size],
    queryFn: () =>
      api<ClearedList>(`/runs/${runId}/reconciliation/cleared?page=${page}&size=${size}`),
    enabled: runId != null,
    placeholderData: keepPreviousData,
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
  })
}

export function useReconciliation(runId: string | null) {
  return useQuery({
    queryKey: ['reconciliation', runId],
    queryFn: () => api<ReconciliationState>(`/runs/${runId}/reconciliation`),
    enabled: runId != null,
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
  })
}

export function useTiming(runId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['timing', runId],
    queryFn: () => api<TimingData>(`/runs/${runId}/timing`),
    // `enabled` is not decoration: these two full-scan the run, and Mantine
    // renders a collapsed Accordion.Panel's children, so an un-opened panel
    // was issuing them on every page load.
    enabled: runId != null && enabled,
  })
}

export function useCreditAtRisk(runId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['credit-at-risk', runId],
    queryFn: () => api<CreditAtRisk>(`/runs/${runId}/credit-at-risk`),
    enabled: runId != null && enabled,
  })
}

export function useProfileFields() {
  return useQuery({
    queryKey: ['profile-fields'],
    queryFn: () => api<ProfileFields>('/format-profiles/fields'),
    staleTime: Infinity,
  })
}

export function useFormatProfiles(clientId: string | null) {
  return useQuery({
    queryKey: ['format-profiles', clientId],
    queryFn: () => api<FormatProfile[]>(`/clients/${clientId}/format-profiles`),
    enabled: clientId != null,
  })
}

export function useInspectUpload(uploadId: string | null) {
  return useQuery({
    queryKey: ['inspect', uploadId],
    queryFn: () => api<InspectResult>(`/uploads/${uploadId}/inspect`),
    enabled: uploadId != null,
    staleTime: Infinity,
    retry: false,
  })
}

export function useRawSummary(periodId: string) {
  return useQuery({
    queryKey: ['raw-summary', periodId],
    queryFn: () => api<RawSummaryEntry[]>(`/periods/${periodId}/raw-summary`),
    enabled: Boolean(periodId),
  })
}

export interface RawRowsParams {
  search?: string
  section?: string
  company?: string
  duplicates?: string
  page: number
  size: number
}

/** The file as a collapsed list of companies — how reconciliation is actually
 *  worked, one company at a time. */
export function useUploadCompanies(uploadId: string | null, search: string,
                                   duplicates = '', sort = 'name',
                                   page = 1, size = 100) {
  const qs = new URLSearchParams()
  if (search) qs.set('search', search)
  if (duplicates) qs.set('duplicates', duplicates)
  qs.set('sort', sort)
  qs.set('page', String(page))
  qs.set('size', String(size))
  return useQuery({
    queryKey: ['upload-companies', uploadId, search, duplicates, sort, page, size],
    queryFn: () => api<RawCompaniesPage>(`/uploads/${uploadId}/companies?${qs}`),
    enabled: uploadId != null,
    placeholderData: keepPreviousData,
  })
}

export function useUploadRows(uploadId: string | null, params: RawRowsParams) {
  const qs = new URLSearchParams()
  if (params.search) qs.set('search', params.search)
  if (params.section) qs.set('section', params.section)
  if (params.company) qs.set('company', params.company)
  if (params.duplicates) qs.set('duplicates', params.duplicates)
  qs.set('page', String(params.page))
  qs.set('size', String(params.size))
  return useQuery({
    queryKey: ['upload-rows', uploadId, params.search ?? '', params.section ?? '',
      params.company ?? '', params.duplicates ?? '', params.page, params.size],
    queryFn: () => api<RawRowsPage>(`/uploads/${uploadId}/rows?${qs}`),
    enabled: uploadId != null,
    placeholderData: keepPreviousData,
  })
}

export function useUploads(periodId: string) {
  return useQuery({
    queryKey: ['uploads', periodId],
    queryFn: () => api<Upload[]>(`/periods/${periodId}/uploads`),
    enabled: Boolean(periodId),
  })
}

export function useRuns(periodId: string) {
  return useQuery({
    queryKey: ['runs', periodId],
    queryFn: () => api<Run[]>(`/periods/${periodId}/runs`),
    enabled: Boolean(periodId),
    // keep polling while any run is still executing
    refetchInterval: (query) =>
      query.state.data?.some((r) => ACTIVE.has(r.status)) ? 2500 : false,
  })
}

export function useRun(runId: string) {
  return useQuery({
    queryKey: ['run', runId],
    queryFn: () => api<Run>(`/runs/${runId}`),
    enabled: Boolean(runId),
    refetchInterval: (query) => (query.state.data && ACTIVE.has(query.state.data.status) ? 2000 : false),
  })
}

export interface PartiesParams {
  category?: string
  search?: string
  page: number
  size: number
}

export function useParties(runId: string, params: PartiesParams, enabled: boolean) {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.search) qs.set('search', params.search)
  qs.set('page', String(params.page))
  qs.set('size', String(params.size))
  return useQuery({
    queryKey: ['parties', runId, params.category ?? '', params.search ?? '', params.page, params.size],
    queryFn: () => api<PageOf<Party>>(`/runs/${runId}/parties?${qs}`),
    enabled: enabled && Boolean(runId),
    placeholderData: keepPreviousData,
  })
}

export interface GridParams {
  category?: string
  flag?: string
  search?: string
  page: number
  size: number
}

export function useGrid(runId: string | null, params: GridParams) {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.flag) qs.set('flag', params.flag)
  if (params.search) qs.set('search', params.search)
  qs.set('page', String(params.page))
  qs.set('size', String(params.size))
  return useQuery({
    queryKey: ['grid', runId, params.category ?? '', params.flag ?? '', params.search ?? '', params.page, params.size],
    queryFn: () => api<GridPage>(`/runs/${runId}/grid?${qs}`),
    enabled: runId != null,
    placeholderData: keepPreviousData,
  })
}

export function usePartyDetail(runId: string, partyId: string) {
  return useQuery({
    queryKey: ['party', runId, partyId],
    queryFn: () => api<PartyDetail>(`/runs/${runId}/parties/${partyId}`),
    enabled: Boolean(runId) && Boolean(partyId),
  })
}

export function useDocuments(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['documents', runId],
    queryFn: () => api<DocumentInfo[]>(`/runs/${runId}/documents`),
    enabled: enabled && Boolean(runId),
  })
}

export function useReportsSummary(runId: string | null) {
  return useQuery({
    queryKey: ['reports-summary', runId],
    queryFn: () => api<ReportsSummary>(`/runs/${runId}/reports/summary`),
    enabled: runId != null,
  })
}

/** `runId` names the run whose membership the names describe. Omitted, the
 *  server answers about the client's latest complete run — which is the right
 *  default for an API and the wrong one for this screen, so the page passes the
 *  run it has open rather than letting the default decide for it. */
export function useAliasGroups(clientId: string, runId?: string | null) {
  return useQuery({
    queryKey: ['alias-groups', clientId, runId ?? null],
    queryFn: () =>
      api<AliasGroup[]>(
        `/clients/${clientId}/alias-groups${runId != null ? `?run=${runId}` : ''}`,
      ),
    enabled: Boolean(clientId),
  })
}
